// Shared carrier enum → label list. One source for every carrier <select> in
// the app (the PO Shipping editor's "Ship via" and the Tracking/Shipments
// record form) so the options never drift. Mirrors the api `carrier` pgEnum.
export const CARRIER_OPTIONS: { value: string; label: string }[] = [
  { value: "fedex", label: "FedEx" },
  { value: "ups", label: "UPS" },
  { value: "usps", label: "USPS" },
  { value: "dhl", label: "DHL" },
  { value: "freight", label: "Freight" },
  { value: "supplier_direct", label: "Supplier direct" },
  { value: "other", label: "Other" },
];

/** Human label for a carrier enum value ("fedex" → "FedEx"); passthrough on
 *  an unknown/empty value. */
export function carrierLabel(v: string | null | undefined): string {
  if (!v) return "";
  return CARRIER_OPTIONS.find((o) => o.value === v)?.label ?? v;
}

/** Ship methods (Stephanie 2026-10-14): "under ship methods we need Ground,
 *  Next Day, 2 Day, 3 Day". Used by the PO's Shipping instructions and the
 *  Shipping tab. */
export const SHIP_SERVICE_LEVELS = ["Ground", "Next Day", "2 Day", "3 Day"] as const;

/** Parcel carriers offered with the ship methods. */
export const PARCEL_CARRIERS = ["UPS", "FedEx", "USPS", "DHL"] as const;

/** "UPS Ground" → { carrier: "UPS", level: "Ground" }; anything else → level only. */
export function splitShipMethod(method: string | null | undefined): { carrier: string; level: string } {
  const t = (method ?? "").trim();
  for (const c of PARCEL_CARRIERS) {
    if (t.toLowerCase().startsWith(c.toLowerCase() + " ")) return { carrier: c, level: t.slice(c.length).trim() };
    if (t.toLowerCase() === c.toLowerCase()) return { carrier: c, level: "" };
  }
  return { carrier: "", level: t };
}

/** { carrier, level } → "UPS Ground" (either part may be empty). */
export function joinShipMethod(carrier: string, level: string): string {
  return [carrier.trim(), level.trim()].filter(Boolean).join(" ");
}

/** The Carrier box that goes with a shipping account's carrier ("ups" → "UPS");
 *  "" when it isn't a parcel carrier (freight, supplier's own account…). */
export function parcelCarrierFor(accountCarrier: string | null | undefined): string {
  const c = (accountCarrier ?? "").trim().toLowerCase();
  return PARCEL_CARRIERS.find((p) => p.toLowerCase() === c) ?? "";
}

/** Picking a shipping account sets the Carrier box to match (Stephanie
 *  2026-11-02: "if i select UPS… the next box should automatically change to
 *  ups"), keeping the speed already chosen. Other accounts leave it alone. */
export function methodForAccount(method: string, accountCarrier: string | null | undefined): string {
  const carrier = parcelCarrierFor(accountCarrier);
  if (!carrier) return method;
  return joinShipMethod(carrier, splitShipMethod(method).level);
}
